import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActiveFilters, FilterEntry } from '../../../hooks/useActiveFilters';
import { FilterBadge, FilterChips } from '../../Shared/Filter/ActiveFilterDisplay';
import { ChevronDoubleRightIcon, ChevronDoubleLeftIcon, MagnifyingGlassIcon, FunnelIcon, ArrowPathIcon, PlusIcon, MinusIcon, CalendarDaysIcon } from "@heroicons/react/20/solid";
import { Disclosure } from '@headlessui/react';
import { clearLocalStorageExceptDark } from '../../TextEditor/textEditorConfig';
import { Link } from "react-router-dom";
import ConfirmModal from '../../Shared/ConfirmModal';
import { RoleType, Skill } from '../../../Models';
import { DeleteRole, UpdateRole } from '../../../api/roles.api';
import Slider from 'rc-slider';
import ReactDatePicker from 'react-datepicker';
import { debounce } from '../../../helpers/constants';
import { fetchAllSkills } from '../../../api/skill.api';
import useApiToken from '../../../hooks/useApiToken';
import { GetRoleTypes } from '../../../api/roles.api';

interface Options {
    id: string;
    name: string;
}

interface FilterSection {
    name: string;
    options: Options[];
}

interface RoleLeftSidebarProps {
    onToggle: (isOpen: boolean) => void;
    handleRoleSearch: (query: string) => void;
    currentPage: number;
    getFilterChange: Function;
    isOpen: boolean;
    selectedRoles: { id: string, iscancel: boolean }[],
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export interface Roleavg {
    id: string;
    avgMin: string | number;
    avgMax: string | number;
}

const RoleLeftSidebar: React.FC<RoleLeftSidebarProps> = ({ onToggle, handleRoleSearch, currentPage, getFilterChange, isOpen, selectedRoles, setLoading }) => {
    const token = useApiToken();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [roletypes, setRoletypes] = useState<RoleType[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[] | Roleavg[]>>({
        'status': ['all'],
        'department': ['all'],
        'skills': ['all'],
        'roletype': ['all'],
        'date': []
    });
    const [skills, setSkils] = useState<Skill[]>([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const selectedFiltersRef = useRef(selectedFilters);
    const searchQueryRef = useRef(searchQuery);
    const getFilterChangeRef = useRef(getFilterChange);

    useEffect(() => {
        selectedFiltersRef.current = selectedFilters;
    }, [selectedFilters]);

    useEffect(() => {
        searchQueryRef.current = searchQuery;
    }, [searchQuery]);

    useEffect(() => {
        getFilterChangeRef.current = getFilterChange;
    }, [getFilterChange]);
    const [cancelOrDeleteCandidate, setCancelOrDeleteCandidate] = useState<boolean | undefined>(undefined);
    const [cancelModalIsOpen, setCancelModalIsOpen] = useState<boolean>(false);
    const [deleteCandidateModalIsOpen, setDeleteCandidateModalIsOpen] = useState<boolean>(false);


    useEffect(() => {
        if (!token) return;
        fetchAllSkills(token).then((res) => setSkils(res));
    }, [token]);

    useEffect(() => {
        GetRoleTypes().then((res) => { setRoletypes(res) });
    }, []);


    useEffect(() => {
        const datefilter: any = selectedFilters.date;
        if (datefilter[0]?.avgMax === "" || datefilter[0]?.avgMin === "") return;
        getFilterChangeRef.current(selectedFilters, searchQueryRef.current);
    }, [selectedFilters]);



    const handleResetFilters = () => {
        setSearchQuery('');
        searchQueryRef.current = '';
        handleRoleSearch('');
        setSelectedFilters({
            'status': ['all'],
            'department': ['all'],
            'skills': ['all'],
            'roletype': ['all'],
            'date': []
        });
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setSelectedFilters(prevFilters => {
            if (!start && !end) {
                return {
                    ...prevFilters,
                    'date': []
                };
            }
            if (end) {
                end.setHours(23, 59, 59, 999);
            }
            return {
                ...prevFilters,
                'date': [{
                    id: 'date-range',
                    avgMin: start ? start.toISOString() : '',
                    avgMax: end ? end.toISOString() : ''
                }]
            };
        });
    };


    const filterSections: FilterSection[] = [
        {
            name: 'status',
            options: [
                { id: 'all', name: 'All' },
                { id: 'active', name: 'Active' },
                { id: 'closed', name: 'Closed' },
                { id: 'cancel', name: 'Cancel' },
            ],
        },
        {
            name: 'department',
            options: [
                { id: 'all', name: 'All' },
                { id: 'it', name: 'IT' },
                { id: 'shipping', name: 'Shipping' },
            ],
        },
        {
            name: 'roletype',
            options: [
                { id: 'all', name: 'All' },
                ...roletypes.map(roletype => ({ id: roletype.id, name: roletype.name }))
            ],
        },
        {
            name: 'skills',
            options: [
                { id: 'all', name: 'All' },
                ...skills.map(skill => ({ id: skill.id, name: skill.name }))
            ],
        },

    ];

    const filterEntries = useMemo<FilterEntry[]>(() => {
        const entries: FilterEntry[] = [];
        Object.entries(selectedFilters).forEach(([key, value]) => {
            if (key === 'date') return;
            const vals = Array.isArray(value) ? (value as string[]) : [];
            if (!vals.includes('all')) {
                const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                const section = filterSections.find(s => s.name === key);
                const displayValue = vals
                    .map(id => section?.options.find(o => o.id === id)?.name ?? id)
                    .join(', ');
                entries.push({ label, active: true, displayValue });
            }
        });
        const dateArr = Array.isArray(selectedFilters['date']) ? (selectedFilters['date'] as any[]) : [];
        const dateRange = dateArr[0];
        if (dateRange?.avgMin || dateRange?.avgMax)
            entries.push({ label: 'Date', active: true, displayValue: 'custom range' });
        return entries;
    }, [selectedFilters, filterSections]);

    const { activeFilterCount, activeFilterNames } = useActiveFilters(filterEntries);

    const handleFilterChange = (sectionName: string, id: string) => {
        setSelectedFilters((prevFilters: any) => {
            if (sectionName === "status") {
                if (id === 'all') {
                    return {
                        ...prevFilters,
                        [sectionName]: ['all'],
                    };
                }
                else {
                    return {
                        ...prevFilters,
                        [sectionName]: [id],
                    };
                }
            }
            else {
                let newSelected = [...prevFilters[sectionName]];
                if (id === 'all') {
                    newSelected = ['all'];
                } else {
                    newSelected = newSelected.filter(item => item !== 'all');
                    if (newSelected.includes(id)) {
                        newSelected = newSelected.filter(item => item !== id);
                    } else {
                        newSelected.push(id);
                    }

                    if (newSelected.length === 0) {
                        newSelected = ['all'];
                    }
                }

                return {
                    ...prevFilters,
                    [sectionName]: newSelected,
                };
            }
        });
    };
    const debouncedSearch = useCallback(
        debounce((query: string) => {
            getFilterChangeRef.current(selectedFiltersRef.current, query);
        }, 1000),
        []
    );


    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedSearch(query);
    };
    const cancelOnConfirm = async () => {
        if (!token) return;
        setLoading(true);
        const body = [
            {
                propertyName: "Iscancel",
                propertyValue: "true",
            }, {
                propertyName: "IsActive",
                propertyValue: String(false),
            }
        ];
        selectedRoles.forEach(async role => {
            await UpdateRole(role.id, body, token);
        });
        setLoading(false);
        window.location.reload();
        setCancelModalIsOpen(false);
    };

    const deleteCandidateOnConfirm = async () => {
        if (!token) return;
        setLoading(true);
        selectedRoles.forEach(async role => {
            await DeleteRole(role.id, token, false);
        });
        setLoading(false);
        window.location.reload();
        setDeleteCandidateModalIsOpen(false);
    };

    useEffect(() => {
        const getCancelOrDeleteCandidate = () => {
            let cancelRoleCount = 0;
            selectedRoles.forEach(role => {
                if (role.iscancel) cancelRoleCount++;
            });

            if (cancelRoleCount === selectedRoles.length) {
                setCancelOrDeleteCandidate(false);
            } else if (cancelRoleCount === 0) {
                setCancelOrDeleteCandidate(true);
            } else {
                setCancelOrDeleteCandidate(undefined);
            }
        }
        getCancelOrDeleteCandidate()
    }, [selectedRoles.length])

    const handleToggle = () => {
        setIsSidebarOpen(!isSidebarOpen);
        onToggle(!isSidebarOpen);
    };
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
    const deleteOnConfirm = async () => {
        if (!token) return;
        setLoading(true);
        selectedRoles.forEach(async role => {
            await DeleteRole(role.id, token, true);
        });
        window.location.reload();
        setLoading(false);
        setDeleteModalIsOpen(false);
    };

    const currentDateRange = (selectedFilters['date'] as Roleavg[])[0];
    return (
        <aside
            className={`select-none fixed z-10 top-[4.1rem] block h-[calc(99vh-4rem)] left-0 shadow-md border dark:border-gray-500 transition-transform transform duration-200 w-[19rem] ${isOpen ? "translate-x-0" : "-translate-x-[100%]"}`}
        >
            <div className="mx-auto bg-gray-100 dark:bg-darkbglight">
                <div className="min-w-0 px-4 pt-2 pb-1">
                    <div className="text-gray-900 dark:text-gray-300 flex items-center space-x-2 justify-center">
                        <h3 className="text-center text-lg font-semibold leading-7 sm:text-base sm:tracking-tight">
                            Role Filters and Actions
                        </h3>
                    </div>
                </div>
            </div>
            <div
                className={`absolute top-10 ${isOpen ? "-right-2 shadow-md" : "-right-4 shadow-xl"} h-16 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer bg-white dark:bg-darkbglight dark:border-gray-500 dark:text-gray-300`}
                onClick={handleToggle}
            >
                {isOpen ? (
                    <ChevronDoubleLeftIcon width={16} />
                ) : (
                    <ChevronDoubleRightIcon width={16} />
                )}
            </div>
            <div className="pb-12 h-full bg-white thin-scroll overflow-y-auto dark:bg-darkbg w-full flex flex-col items-center mt-2">
                <div className="relative flex focus-within:z-10 mt-2 items-center justify-between w-[90%] mb-4">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                        <MagnifyingGlassIcon
                            className="h-5 w-5 text-gray-400 dark:text-gray-300"
                            aria-hidden="true"
                        />
                    </div>
                    <input
                        type="text"
                        name="search"
                        id="search"
                        className="block w-10/12 border-0 pl-9 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
                        placeholder="Quick Search"
                        onChange={handleSearchChange}
                        value={searchQuery}
                    />
                    <div
                        className="text-gray-400 hover:text-gray-500 cursor-pointer relative dark:hover:text-gray-300"
                        onClick={handleResetFilters}
                        title="Reset all filters"
                    >
                        <FunnelIcon width={24} />
                        <ArrowPathIcon width={14} className="absolute bottom-0 -right-1" />
                        <FilterBadge count={activeFilterCount} />
                    </div>
                </div>
                <FilterChips names={activeFilterNames} />

                {filterSections.map((section, index: number) => (
                    <Disclosure as="div" key={index} className="w-[90%] border-b border-gray-200 dark:border-gray-500">
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="flex w-full justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 font-semibold">
                                    <span className="text-gray-900 dark:text-gray-400">
                                        {section.name
                                            .replace(/([A-Z][a-z]+)/g, " $1")
                                            .split(" ")
                                            .filter((word, index, arr) => index === 0 || index !== arr.length - 1)
                                            .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
                                            .join(" ")} {/* Rejoin the words */}
                                    </span>
                                    {open ? (
                                        <MinusIcon className="h-5 w-5 text-gray-200" />
                                    ) : (
                                        <PlusIcon className="h-5 w-5 text-gray-200" />
                                    )}
                                </Disclosure.Button>
                                <Disclosure.Panel className="pb-2 text-sm text-gray-500 dark:text-gray-400">
                                    {section.options.map((option) => (
                                        <div key={option.id} className="flex items-center mb-2">
                                            <input
                                                id={`${section.name}-${option.id}`}
                                                name={`${section.name}[]`}
                                                type="checkbox"
                                                checked={Array.isArray(selectedFilters[section.name]) && (selectedFilters[section.name] as string[]).includes(option.id)}
                                                onChange={() => handleFilterChange(section.name, option.id)}
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <label
                                                htmlFor={`${section.name}-${option.id}`}
                                                className="ml-3 text-sm text-gray-600 dark:text-gray-400"
                                            >
                                                {option.name}
                                            </label>
                                        </div>
                                    ))
                                    }
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                ))}

                <div className="py-4 px-4 w-full">
                    <h3 className=" flow-root">
                        <div className="flex w-full items-center justify-between py-3 text-sm text-gray-600 hover:text-gray-500">
                            <span className="font-medium text-gray-900 dark:text-gray-300">
                                Date
                            </span>
                            <span className="flex items-center">
                                <div className="flex items-center">
                                    <ReactDatePicker
                                        onChange={(dates) => handleDateChange(dates as [Date | null, Date | null])}
                                        selectsRange
                                        isClearable
                                        showIcon
                                        showYearDropdown
                                        scrollableYearDropdown
                                        dropdownMode="select"
                                        startDate={currentDateRange?.avgMin ? new Date(currentDateRange?.avgMin) : null}
                                        endDate={currentDateRange?.avgMax ? new Date(currentDateRange?.avgMax) : null}
                                        icon={<CalendarDaysIcon className="text-gray-400" />}
                                        className="placeholder:text-gray-400 text-sm rounded-md border-none ring-1 ring-inset ring-gray-300 block w-[13rem] align-bottom cursor-pointer py-1 dark:bg-darkbg dark:ring-gray-500"
                                        placeholderText="  Select period"
                                        calendarClassName="datepicker-calendar"
                                        wrapperClassName="datepicker-wrapper"
                                    />
                                </div>
                            </span>
                        </div>
                    </h3>
                </div>
                <div>
                    <div className="flex items-center flex-col gap-5 mt-4 p-2">
                        <Link to="/roles/create" className="sm:ml-4 sm:mt-0">
                            <button
                                type="button"
                                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={() => clearLocalStorageExceptDark()}
                            >
                                Create new job
                            </button>
                        </Link>

                        <Link to="/roles/calendar" className="mt-3 sm:ml-4 sm:mt-0">
                            <div className="flex items-center px-3 py-2 rounded-md  font-semibold text-gray-800 dark:text-gray-300 ">
                                <CalendarDaysIcon className="h-6 w-6 aria-hidden:true" />
                                <div className="pl-2 ">
                                    Calendar
                                </div>
                            </div>
                        </Link>
                    </div>
                    {selectedRoles.length > 0 &&
                        <div className='flex flex-col gap-4 items-center justify-center py-4'>
                            <button
                                type="button"
                                className="flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={() => setDeleteModalIsOpen(true)}
                            >
                                Delete Selected Roles
                            </button>
                            {cancelOrDeleteCandidate && <button
                                type="button"
                                className="flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={() => setCancelModalIsOpen(true)}
                            >
                                Cancel Selected Roles
                            </button>}
                            {cancelOrDeleteCandidate != undefined && !cancelOrDeleteCandidate && <button
                                type="button"
                                className="flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={() => setDeleteCandidateModalIsOpen(true)}
                            >
                                Delete Candidates Of Selected Roles
                            </button>}
                        </div>
                    }
                </div>
                <div
                    className={`absolute top-10 ${isSidebarOpen ? "-left-2 shadow-md" : "-left-4 shadow-xl"} h-16 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer bg-white dark:bg-darkbglight dark:border-gray-500 dark:text-gray-300`}
                    onClick={handleToggle}
                >
                </div>
                <ConfirmModal
                    open={deleteModalIsOpen}
                    setOpen={setDeleteModalIsOpen}
                    onConfirm={deleteOnConfirm}
                    type="danger"
                    title="Delete jobs"
                    description=" Are you sure you want to delete the selected jobs? All of the
                                data will be permanently removed. This action cannot
                                be undone."
                />
                <ConfirmModal
                    open={deleteCandidateModalIsOpen}
                    setOpen={setDeleteCandidateModalIsOpen}
                    onConfirm={deleteCandidateOnConfirm}
                    type="danger"
                    title="Delete all related candidates"
                    description=" Are you sure you want to delete all related candidates of the selected jobs? All of the
                                data will be permanently removed. This action cannot
                                be undone."
                />
                <ConfirmModal
                    open={cancelModalIsOpen}
                    setOpen={setCancelModalIsOpen}
                    onConfirm={cancelOnConfirm}
                    type="danger"
                    title="Cancel job"
                    description=" Are you sure you want to cancel the selected jobs? This action cannot
                                be undone."
                />
            </div>
        </aside>
    );
};

export default RoleLeftSidebar;